import {
  HighlightIcon,
  SwitchField,
  Table,
  Tag,
  Tooltip,
} from '@dev-spendesk/grapes';
import React, { type ReactNode, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { type Company, useCompany } from 'modules/app/hooks/useCompany';
import { UserAvatar } from 'modules/users/components';
import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';
import { routeFor, routes } from 'src/core/constants/routes';
import {
  getCustomFieldsWithArchiveStatus,
  type EnrichedPayableRule,
  type EnrichedValueFilter,
} from 'src/core/modules/payable';

import { PayableRulesTableActions } from './PayableRulesTableActions';
import {
  getCostCenterTag,
  getCustomFieldTag,
  getGrossAmountTag,
  getIconVariantFromName,
  getPayableTypeTag,
  getRequestorTag,
  getSupplierTag,
  type TagCollection,
} from './utils';
import { useUpdatePayableRulesStateFromTable } from './utils/useUpdatePayableRulesStateFromTable';

type Props = {
  enrichedPayableRules: EnrichedPayableRule[];
  selectedPayableRuleId: string | undefined;
};

export const PayableRulesTable = ({
  enrichedPayableRules,
  selectedPayableRuleId,
}: Props) => {
  const { t } = useTranslation('global');
  const history = useHistory();
  const company = useCompany();
  const [selectedRowIds, setSelectedRowsIds] = useState<string[]>([]);

  const updatePayableRulesStateFromTable = useUpdatePayableRulesStateFromTable(
    company.id,
  );

  return (
    <>
      <Table
        className="my-24"
        columns={[
          {
            id: 'name',
            width: '23.45%',
            renderCell: (payableRule: EnrichedPayableRule) => (
              <div className="flex items-center gap-8">
                {!!payableRule.problems.length && (
                  <Tooltip
                    content={payableRule.problems[0]}
                    placement="right"
                    triggerAsChild
                  >
                    <HighlightIcon
                      size={32}
                      name="triangle-warning"
                      variant="warning"
                    />
                  </Tooltip>
                )}
                {!payableRule.problems.length && (
                  <HighlightIcon
                    size={32}
                    name={payableRule.icon}
                    variant={getIconVariantFromName(payableRule.icon)}
                  />
                )}
                <div>
                  {payableRule.displayName}
                  {!!payableRule.problems.length && (
                    <div className="text-content-secondary-bg-primary">
                      {t(
                        'bookkeep.integrations.settings.payableRules.errors.problematicRule',
                      )}
                    </div>
                  )}
                </div>
              </div>
            ),
            getSortValue: (payableRule) =>
              payableRule.displayName ?? payableRule.name,
            header: t('bookkeep.integrations.settings.payableRules.table.name'),
          },
          {
            id: 'conditions',
            renderCell: (payableRule: EnrichedPayableRule) => (
              <div className="flex items-center gap-4 truncate">
                {convertConditions({
                  payableRule,
                  company,
                  translator: t,
                })}
              </div>
            ),
            header: t(
              'bookkeep.integrations.settings.payableRules.table.conditions',
            ),
          },
          {
            id: 'outputs',
            renderCell: (payableRule: EnrichedPayableRule) => (
              <div className="flex items-center gap-4 truncate">
                {convertOutputs({
                  outputs: payableRule.outputs,
                  translator: t,
                })}
              </div>
            ),
            header: t(
              'bookkeep.integrations.settings.payableRules.table.outputs',
            ),
          },
          {
            id: 'by',
            width: '72px',
            className: 'p-0 pl-12 pr-4 centered-column',
            align: 'center',
            renderCell: (payableRule: EnrichedPayableRule) => {
              const user = payableRule.updatedByUser;
              if (!user) {
                return (
                  <Tooltip
                    content={t(
                      'bookkeep.integrations.settings.payableRules.defaultUserNameTooltip',
                    )}
                    placement="top"
                    triggerAsChild
                  >
                    <div className="flex items-center justify-center pr-8">
                      <UserAvatar
                        id="userId"
                        name={t(
                          'bookkeep.integrations.settings.payableRules.defaultUserName',
                        )}
                      />
                    </div>
                  </Tooltip>
                );
              }
              return (
                <Tooltip content={user.fullname} placement="top" triggerAsChild>
                  <div className="flex items-center justify-center pr-8">
                    <UserAvatar id={user.id} name={user.fullname} />
                  </div>
                </Tooltip>
              );
            },
            getSortValue: (payableRule) =>
              payableRule.updatedByUser?.fullname || '',
            header: t('bookkeep.integrations.settings.payableRules.table.by'),
          },
          {
            id: 'isActive',
            width: '88px',
            className: 'p-0 pl-12 pr-4 centered-column',
            align: 'center',
            renderCell: (payableRule: EnrichedPayableRule) => (
              <SwitchField
                label=""
                className="centered-toggle items-center pr-16"
                isChecked={payableRule.state === 'published'}
                // @ts-expect-error GRAPES HACKING TIME - we need this to prevent onRowClick to go through
                onClick={(event: React.ChangeEvent<HTMLInputElement>) => {
                  event.stopPropagation();
                  event.preventDefault();
                }}
                onChange={async () => {
                  await updatePayableRulesStateFromTable(
                    [payableRule.id],
                    payableRule.state === 'published' ? 'unpublish' : 'publish',
                  );
                }}
              />
            ),
            getSortValue: (payableRule) => payableRule.state,
            header: t(
              'bookkeep.integrations.settings.payableRules.table.isActive',
            ),
          },
        ]}
        data={enrichedPayableRules}
        selectedRowIds={selectedRowIds}
        getRowId={(payableRule) => payableRule.id}
        getIsRowActive={(payableRule) =>
          selectedPayableRuleId === payableRule.id
        }
        onRowSelectionChange={(_, id, checked) => {
          setSelectedRowsIds((options) => {
            if (checked) {
              return options.concat(id);
            }
            return options.filter((optionId) => optionId !== id);
          });
        }}
        onAllRowsSelectionChange={(_, ids, checked) => {
          setSelectedRowsIds(checked ? ids : []);
        }}
        onRowClick={(payableRule) => {
          history.push(
            routeFor(routes.COMPANY_ACCOUNTING_PAYABLE_RULES_SETUP.path, {
              company: company.id,
              payableRuleId: payableRule.id,
            }),
          );
        }}
      />
      <PayableRulesTableActions
        companyId={company.id}
        selectedPayableRules={enrichedPayableRules.filter((rule) =>
          selectedRowIds.includes(rule.id),
        )}
        resetSelectedPayableRules={() => {
          setSelectedRowsIds([]);
        }}
      />
    </>
  );
};

function convertConditions({
  payableRule,
  company,
  translator,
}: {
  company: Company;
  payableRule: EnrichedPayableRule;
  translator: TGlobalFunctionTyped;
}) {
  const conditionsTags: React.ReactNode[] = [];
  const conditionsTooltipItems: React.ReactNode[] = [];

  payableRule.conditions.forEach((condition) => {
    // in order to support deleted fields we inrtoduced new way tp redner tags
    // tagContent should be removed once we have all tags implemented
    let tagContent;
    let tagCollection: TagCollection | undefined;
    if (condition.field === 'supplier') {
      tagCollection = getSupplierTag({
        condition: condition as EnrichedValueFilter<'supplier'>,
        translator,
      });
    }
    if (condition.field === 'costCenter') {
      tagCollection = getCostCenterTag({
        condition: condition as EnrichedValueFilter<'costCenter'>,
        translator,
      });
    }
    if (condition.field === 'requestor') {
      tagCollection = getRequestorTag({
        condition: condition as EnrichedValueFilter<'requestor'>,
        translator,
      });
    }
    // Payable type is the only field left in old format
    // but i can not have deleted values
    // probably we should refactor it to avoid double implementation
    if (condition.field === 'payableType') {
      tagContent = getPayableTypeTag({
        condition,
        translator,
      });
    }

    if (condition.field === 'grossAmount') {
      tagContent = getGrossAmountTag({ condition, currency: company.currency });
    }

    if (condition.field === 'customField') {
      tagCollection = getCustomFieldTag({
        condition,
        translator,
      });
    }

    if (tagCollection && tagCollection.tags.length > 0) {
      // check if all values are archived or custom field is archived
      conditionsTags.push(
        buildTagFromCollection(payableRule, condition, tagCollection),
      );
      conditionsTooltipItems.push(
        <li
          className="break-words"
          key={`${payableRule.id}-${condition.field}-${condition.operator}`}
        >
          {tagCollection.tags.map(({ textContent }) => textContent).join('/')}
        </li>,
      );
    }
    if (tagContent) {
      conditionsTags.push(
        <Tag
          key={`${payableRule.id}-${condition.field}-${condition.operator}-${tagContent}`}
          variant="carbon"
        >
          {tagContent}
        </Tag>,
      );
      conditionsTooltipItems.push(
        <li className="break-words" key={tagContent}>
          {tagContent}
        </li>,
      );
    }
  });

  return (
    <Tooltip
      content={
        <p className="automation-rule-tooltip">
          <ul>{conditionsTooltipItems}</ul>
        </p>
      }
      placement="top"
      triggerAsChild
    >
      <p className="truncate">{conditionsTags}</p>
    </Tooltip>
  );
}

function convertOutputs({
  outputs,
  translator,
}: {
  outputs: EnrichedPayableRule['outputs'];
  translator: TGlobalFunctionTyped;
}) {
  const outputsTags: React.ReactNode[] = [];
  const outputsTooltipItems: React.ReactNode[] = [];

  const expenseAccount =
    typeof outputs.expenseAccount === 'object'
      ? outputs.expenseAccount
      : undefined;
  const vatAccount =
    typeof outputs.vatAccount === 'object' ? outputs.vatAccount : undefined;
  const costCenter =
    typeof outputs.costCenter === 'object' ? outputs.costCenter : undefined;
  const customFields =
    (outputs.customField &&
      getCustomFieldsWithArchiveStatus(outputs.customField)) ||
    [];

  if (expenseAccount) {
    outputsTags.push(renderOutcomeTag(expenseAccount));
    outputsTooltipItems.push(
      <li className="break-words">{expenseAccount.name}</li>,
    );
  }

  if (vatAccount) {
    outputsTags.push(renderOutcomeTag(vatAccount));
    outputsTooltipItems.push(
      <li className="break-words">{vatAccount.name}</li>,
    );
  }

  if (costCenter) {
    const costCenterWithArchiveStatus = {
      ...costCenter,
      isArchived: costCenter.isDeleted ?? false,
    };
    outputsTags.push(renderOutcomeTag(costCenterWithArchiveStatus));
    outputsTooltipItems.push(
      <li className="break-words">{costCenter.name}</li>,
    );
  }

  if (customFields && customFields.length > 0) {
    customFields.forEach((field) => {
      const updatedField = {
        ...field,
        name:
          field.type === 'Boolean'
            ? `${field.name}: ${field.value === 'true' ? translator('misc.yes').toLowerCase() : translator('misc.no').toLowerCase()}`
            : field.name,
      };
      outputsTags.push(
        renderOutcomeTag(
          updatedField,
          JSON.stringify(String(updatedField.name)),
        ),
      );
      outputsTooltipItems.push(<li>{updatedField.name}</li>);
    });
  }

  return (
    <Tooltip
      content={
        <p className="automation-rule-tooltip">
          <ul>{outputsTooltipItems}</ul>
        </p>
      }
      placement="top"
      triggerAsChild
    >
      <p className="truncate">{outputsTags}</p>
    </Tooltip>
  );
}

function buildTagFromCollection(
  payableRule: EnrichedPayableRule,
  condition: EnrichedPayableRule['conditions'][number],
  tagCollection: TagCollection,
): ReactNode {
  // check if all values are archived or custom field is archived
  const isArchivedGlobal =
    tagCollection.isArchived || tagCollection.tags.every((t) => t.isArchived);

  return (
    <Tag
      key={`${payableRule.id}-${condition.field}-${condition.operator}-${tagCollection.tags.map((t) => t.textContent).join(',')}`}
      variant={
        tagCollection.isProblematic || tagCollection.isArchived
          ? 'warning'
          : 'carbon'
      }
      iconName={
        tagCollection.isProblematic || tagCollection.isArchived
          ? 'triangle-warning'
          : undefined
      }
      className={[
        isArchivedGlobal ? 'crossed-out' : '',
        tagCollection.isProblematic ? 'problematic' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {isArchivedGlobal &&
        tagCollection.tags.map(({ textContent }) => textContent).join('/')}
      {!isArchivedGlobal &&
        tagCollection.tags.map(({ textContent, isArchived }, index) => (
          <React.Fragment
            key={`${payableRule.id}-${condition.field}-${condition.operator}-${textContent}`}
          >
            <span className={isArchived ? 'crossed-out' : ''}>
              {textContent}
            </span>
            {index < tagCollection.tags.length - 1 && (
              <span className="mx-1">/</span>
            )}
          </React.Fragment>
        ))}
    </Tag>
  );
}

type OutcomeItem = {
  name: string;
  isArchived?: boolean;
};

function renderOutcomeTag(item: OutcomeItem, key?: string) {
  return item.isArchived ? (
    <Tag
      iconName="triangle-warning"
      variant="warning"
      className="crossed-out"
      key={key}
    >
      {item.name}
    </Tag>
  ) : (
    <Tag variant="carbon" key={key}>
      {item.name}
    </Tag>
  );
}
